/**
 * Utility functions for parsing, validating, and generating color variations
 * Supports Hex (#RRGGBB, #RGB) and RGB/RGBA (rgb(r,g,b), rgba(r,g,b,a))
 */

export function parseColor(colorStr) {
  if (!colorStr || typeof colorStr !== "string") {
    return null;
  }
  const str = colorStr.trim();

  // Hex format
  if (str.startsWith("#")) {
    const hex = str.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return { r, g, b, a: 1, hex: `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}` };
    }
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return { r, g, b, a: 1, hex: `#${hex}` };
    }
    if (hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const a = parseInt(hex.slice(6, 8), 16) / 255;
      return { r, g, b, a, hex: `#${hex.slice(0, 6)}` };
    }
  }

  // RGB / RGBA format
  const rgbMatch = str.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/i);
  if (rgbMatch) {
    const r = Math.min(255, Math.max(0, parseInt(rgbMatch[1], 10)));
    const g = Math.min(255, Math.max(0, parseInt(rgbMatch[2], 10)));
    const b = Math.min(255, Math.max(0, parseInt(rgbMatch[3], 10)));
    const a = rgbMatch[4] !== undefined ? Math.min(1, Math.max(0, parseFloat(rgbMatch[4]))) : 1;
    const toHex = (n) => n.toString(16).padStart(2, "0");
    return { r, g, b, a, hex: `#${toHex(r)}${toHex(g)}${toHex(b)}` };
  }

  return null;
}

export function isValidColor(colorStr) {
  return parseColor(colorStr) !== null;
}

export function toRgbaString(colorStr, alpha = 1) {
  const parsed = parseColor(colorStr);
  if (!parsed) return colorStr;
  return `rgba(${parsed.r}, ${parsed.g}, ${parsed.b}, ${alpha})`;
}

export function darkenColor(colorStr, percent = 15) {
  const parsed = parseColor(colorStr);
  if (!parsed) return colorStr;
  const factor = (100 - percent) / 100;
  const r = Math.max(0, Math.min(255, Math.round(parsed.r * factor)));
  const g = Math.max(0, Math.min(255, Math.round(parsed.g * factor)));
  const b = Math.max(0, Math.min(255, Math.round(parsed.b * factor)));
  const toHex = (n) => n.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function lightenColor(colorStr, percent = 20) {
  const parsed = parseColor(colorStr);
  if (!parsed) return colorStr;
  const factor = percent / 100;
  const r = Math.max(0, Math.min(255, Math.round(parsed.r + (255 - parsed.r) * factor)));
  const g = Math.max(0, Math.min(255, Math.round(parsed.g + (255 - parsed.g) * factor)));
  const b = Math.max(0, Math.min(255, Math.round(parsed.b + (255 - parsed.b) * factor)));
  const toHex = (n) => n.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function generateGradient(color1, color2) {
  if (color1 && color2) {
    const p1 = parseColor(color1);
    const p2 = parseColor(color2);
    const c1 = p1 ? p1.hex : color1;
    const c2 = p2 ? p2.hex : color2;
    return `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`;
  }
  const parsed = parseColor(color1);
  if (!parsed) return `linear-gradient(135deg, #312e81 0%, #4f46e5 50%, #3b82f6 100%)`;
  const dark = darkenColor(color1, 28);
  const mid = parsed.hex;
  const light = lightenColor(color1, 22);
  return `linear-gradient(135deg, ${dark} 0%, ${mid} 50%, ${light} 100%)`;
}

/**
 * Calculates WCAG relative luminance of a color string (Hex or RGBA)
 * Range: 0 (pure black) to 1 (pure white)
 */
export function getLuminance(colorStr) {
  const parsed = parseColor(colorStr);
  if (!parsed) return 0.5;
  const [r, g, b] = [parsed.r, parsed.g, parsed.b].map((c) => {
    const val = c / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Returns the optimal readable text color (white or dark charcoal) based on background luminance
 */
export function getContrastTextColor(colorStr, darkColor = "#0f172a", lightColor = "#ffffff") {
  const lum = getLuminance(colorStr);
  return lum > 0.4 ? darkColor : lightColor;
}

/**
 * Returns the optimal readable text color for dual-color gradients
 */
export function getGradientContrastTextColor(color1, color2, darkColor = "#0f172a", lightColor = "#ffffff") {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const avgLum = (lum1 + lum2) / 2;
  return avgLum > 0.4 ? darkColor : lightColor;
}

